import MicButton from '@/components/mic/MicButton';
import type { MicState } from '@/lib/mic/useMicController';
import { COACH } from '@/constants/coach';

/**
 * Shared "Ask the coach anything" input bar — type or mic. The coach's text/mic
 * surfaces are identical across LessonScreen and PlaybookScreen (and any
 * future coach screens), so the markup lives here. The host owns the
 * MicController; the pre-prompt and error overlays render at the host's
 * screen level, not inside this component.
 */

interface AskCoachInputProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onMicTap: () => void;
  micState: MicState;
  disabled: boolean;
}

export default function AskCoachInput({
  value,
  onChange,
  onSubmit,
  onMicTap,
  micState,
  disabled,
}: AskCoachInputProps) {
  const placeholder = disabled
    ? `${COACH.firstName} is loading…`
    : micState === 'listening'
      ? 'Listening… speak naturally, then tap the mic to stop.'
      : micState === 'committing'
        ? 'Sending…'
        : `Type a question — ${COACH.firstName} will answer out loud.`;

  return (
    <div className="ask-coach">
      <p className="ask-coach__eyebrow">Ask {COACH.firstName} anything</p>
      <form
        className="ask-coach__row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!value.trim()) return;
          onSubmit();
        }}
      >
        <MicButton state={micState} onTap={onMicTap} disabled={disabled} size="sm" />
        <input
          type="text"
          className="ask-coach__input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <button type="submit" className="ask-coach__send" disabled={disabled || !value.trim()}>
          Ask <ArrowRight />
        </button>
      </form>
    </div>
  );
}

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
